/**
 * O preview do certificado. Agora desenha o que o pipeline decidiu.
 *
 * A ASSINATURA NÃO MUDOU. As oito telas que chamam este componente continuam
 * passando as mesmas props; o que mudou é quem decide o desenho. Antes eram 687
 * linhas decidindo aqui — e o worker de e-mail decidindo de novo, do seu jeito,
 * noutro arquivo. Chegaram a 15 divergências ativas, e não por azar: uma regra
 * escrita em dois lugares diverge, é aritmética.
 *
 * Agora as props viram um snapshot, o `resolve` produz a lista de instruções, e
 * o `CertificateCanvas` pinta. O `renderPdf` do servidor recebe a MESMA lista.
 * Não sobrou decisão para os dois discordarem.
 *
 * O renderizador antigo continua em `CertificatePreviewLegado`, atrás de flag,
 * para a comparação lado a lado ser possível durante a migração.
 *
 * OS TIPOS FICAM AQUI
 *
 * `CertificateFields`, `CertificateSampleData` e os outros são importados por
 * outras sete telas e por `lib/certificate-layout.ts`. Movê-los agora
 * espalharia o diff sem ganho — eles saem quando as telas migrarem para o
 * snapshot direto.
 */

import { memo, useMemo } from "react";

import { resolve, type ElementId } from "@tickethall/core/certificate";

import { CertificateCanvas } from "./CertificateCanvas";
import { CERTIFICATE_WIDTH, CERTIFICATE_HEIGHT } from "./CertificateVector";
import { snapshotDasProps, manifestoDasProps } from "./CertificatePreviewAdapter";
import { useDimensoesDeImagem } from "./useDimensoesDeImagem";
import { CertificatePreviewLegado } from "./CertificatePreviewLegado";
import type { CertificateElement, CertificateElementId } from "./CertificateElementTypes";

export { CERTIFICATE_WIDTH, CERTIFICATE_HEIGHT };

export interface CertificateFields {
  showEventName: boolean;
  showParticipantName: boolean;
  showParticipantLastName: boolean;
  showCPF: boolean;
  maskCPF?: boolean;
  showEventDate: boolean;
  eventDateMode?: "start" | "end" | "range";
  showEventTime?: boolean;
  showEventLocation: boolean;
  showWorkload: boolean;
  showSigners: boolean;
  showQRCode?: boolean;
}

export interface CertificateTextConfig {
  title: string;
  introText: string;
  participationText: string;
  conclusionText: string;
}

export interface CertificateSigner {
  name: string;
  role: string;
  signatureUrl?: string;
}

export interface CertificateSampleData {
  eventName: string;
  participantName: string;
  participantCPF: string;
  eventDate: string;
  eventTime?: string;
  eventLocation: string;
  certificateCode: string;
}

export interface CertificateFontSizes {
  title: "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  name: "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  event: "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl";
}

export interface CertificateTextPositions {
  titleTop: number;
  nameTop: number;
  metadataTop: number;
}

export interface CertificatePreviewProps {
  backgroundUrl?: string;
  fields: CertificateFields;
  textConfig: CertificateTextConfig;
  signers: CertificateSigner[];
  workloadHours?: number;
  sampleData: CertificateSampleData;
  textColor?: string;
  textPositions?: CertificateTextPositions;
  fontSizes?: CertificateFontSizes;
  elements?: CertificateElement[];
  className?: string;
  onPreviewReady?: () => void;
  mode?: "producer" | "production";
  onElementMove?: (id: CertificateElementId, position: { x: number; y: number }) => void;
  selectedElementId?: CertificateElementId | null;
  onSelectElement?: (id: CertificateElementId | null) => void;

  /**
   * Datas em ISO e fuso do evento.
   *
   * Sem elas, o adaptador reconstrói a data a partir da string já formatada em
   * `sampleData`, assumindo fuso brasileiro. Quem tiver o ISO deve passar.
   */
  eventStartsAt?: string | null;
  eventEndsAt?: string | null;
  timeZone?: string;
  organizerName?: string | null;

  /**
   * Volta ao renderizador antigo.
   *
   * Existe para a página de comparação mostrar os dois lado a lado. Não use em
   * tela de produto: o antigo é a fonte das 15 divergências.
   */
  usarRenderizadorAntigo?: boolean;
}

export const CertificatePreview = memo(function CertificatePreview(props: CertificatePreviewProps) {
  const {
    className, mode, onElementMove, selectedElementId, onSelectElement, onPreviewReady,
    usarRenderizadorAntigo,
    backgroundUrl, fields, textConfig, signers, workloadHours, sampleData, textColor,
    textPositions, fontSizes, elements, eventStartsAt, eventEndsAt, timeZone, organizerName,
  } = props;

  // Cada campo listado, e não um `...resto` espalhado: com o objeto de resto o
  // ESLint não consegue conferir as dependências, e uma prop esquecida vira
  // preview desatualizado — o produtor mexe e a tela não acompanha.
  const entrada = useMemo(
    () => ({
      backgroundUrl, fields, textConfig, signers, workloadHours, sampleData, textColor,
      textPositions, fontSizes, elements, eventStartsAt, eventEndsAt, timeZone, organizerName,
    }),
    [
      backgroundUrl, fields, textConfig, signers, workloadHours, sampleData, textColor,
      textPositions, fontSizes, elements, eventStartsAt, eventEndsAt, timeZone, organizerName,
    ],
  );

  /*
   * As imagens precisam ser MEDIDAS antes de entrar no manifesto.
   *
   * O `resolve` escala a assinatura pela proporção do arquivo. Declarar zero
   * fazia a conta virar NaN e a assinatura sumir do preview sem erro nenhum —
   * enquanto o PDF do e-mail, que tem as medidas de verdade, a desenhava.
   */
  const urlsDeImagem = useMemo(
    () =>
      [backgroundUrl, ...(signers ?? []).map((s) => s.signatureUrl)].filter(
        (u): u is string => !!u,
      ),
    [backgroundUrl, signers],
  );
  const dimensoes = useDimensoesDeImagem(urlsDeImagem);

  // O fundo entra pelo MANIFESTO, não como imagem atrás do desenho: sem asset
  // no manifesto o `resolve` pinta um retângulo opaco por cima de tudo, e a
  // imagem sumiria embaixo dele.
  const { manifest, urlDoAsset } = useMemo(
    () => manifestoDasProps(entrada, dimensoes),
    [entrada, dimensoes],
  );
  const resolvido = useMemo(() => resolve(snapshotDasProps(entrada), manifest), [entrada, manifest]);

  if (usarRenderizadorAntigo) {
    return <CertificatePreviewLegado {...props} />;
  }

  return (
    <CertificateCanvas
      resolved={resolvido}
      assetUrl={urlDoAsset}
      mode={mode}
      selectedElementId={selectedElementId as ElementId | null | undefined}
      onSelectElement={onSelectElement as ((id: ElementId | null) => void) | undefined}
      onElementMove={onElementMove as ((id: ElementId, p: { x: number; y: number }) => void) | undefined}
      className={className}
      onPreviewReady={onPreviewReady}
    />
  );
});
